import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import {
  changePassword,
  requestPasswordReset,
  resetPassword,
  verifyPasswordResetToken,
} from '@/lib/apis';

export const useRequestPasswordReset = () => {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (email: string) => requestPasswordReset(email),
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unable to send reset email. Please try again.';
      toast({
        title: 'Password Reset Failed',
        description: errorMessage,
      });
    },
  });

  return {
    requestReset: mutation.mutate,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};

export const useVerifyResetToken = (token: string | undefined) => {
  return useQuery({
    queryKey: ['verify-reset-token', token],
    queryFn: () => verifyPasswordResetToken(token!),
    enabled: !!token,
    retry: false,
    staleTime: 0,
  });
};

export const useResetPassword = (onSuccess?: () => void) => {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      resetPassword(token, newPassword),
    onSuccess: () => {
      toast({
        title: 'Password Updated',
        description: 'You can now sign in with your new password.',
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unable to reset password. Please try again.';
      toast({
        title: 'Password Reset Failed',
        description: errorMessage,
      });
    },
  });

  return {
    resetPassword: mutation.mutate,
    isLoading: mutation.isPending,
  };
};

export const useChangePassword = (onSuccess?: () => void) => {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      changePassword(currentPassword, newPassword),
    onSuccess: () => {
      toast({
        title: 'Password Updated',
        description: 'Your password has been changed.',
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unable to change password. Please try again.';
      toast({
        title: 'Password Change Failed',
        description: errorMessage,
      });
    },
  });

  return {
    changePassword: mutation.mutate,
    isLoading: mutation.isPending,
  };
};
